// src/admin/pages/NewMember.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";

const TIERS = ["Explore", "Adventure", "Elite", "Platinum"];
const BILLING = ["None", "Monthly", "Annual"];
const STATUSES = ["prospect", "active", "paused", "cancelled"];



const DAYS_BY_TIER = {
  Explore: 15,
  Adventure: 22,
  Elite: 29,
  Platinum: 40,
};

const DOC_TYPES = {
  proof_of_insurance: "proof_of_insurance",
  drivers_license_front: "drivers_license_front",
  drivers_license_back: "drivers_license_back",
  drivers_abstract: "drivers_abstract",
  boating_license: "boating_license",
  other: "other",
};

function normalize(s) {
  return String(s || "").trim();
}
function toNull(s) {
  const v = normalize(s);
  return v ? v : null;
}
function toInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? null : n;
}
function ymd(date) {
  // expects "YYYY-MM-DD" from <input type="date" />
  return date ? String(date) : null;
}
function safeLowerEmail(v) {
  const s = normalize(v);
  return s ? s.toLowerCase() : null;
}
function fileExt(name = "") {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : "bin";
}
function makeSafeFileName(name = "file") {
  return name.replace(/[^\w.-]+/g, "_");
}

function formatSbError(e) {
  if (!e) return null;
  return {
    message: e.message || "Unknown error",
    code: e.code || null,
    details: e.details || null,
    hint: e.hint || null,
  };
}

function newDriver(isPrimary = false) {
  return {
    is_primary: isPrimary,

    first_name: "",
    last_name: "",
    phone: "",
    email: "",

    driver_license_number: "",
    driver_license_expiry: "", // yyyy-mm-dd
    driver_license_province: "",

    abstract_required: false,

    boating_license_number: "",
    boating_license_expiry: "", // yyyy-mm-dd
  };
}

export default function NewMember() {
  const nav = useNavigate();

  const [busy, setBusy] = useState(false);
  const [errorObj, setErrorObj] = useState(null);

  // helpful debug so you can see partial progress
  const [created, setCreated] = useState({
    contactId: null,
    membershipId: null,
    driverIds: [],
  });

  // Core form
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    source: "call-in",

    // new fields
    date_of_birth: "",

    // FORMAT (stored as jsonb)
    format_family: "",
    format_occupation: "",
    format_recreation: "",
    format_motivation: "",
    format_animals: "",
    format_teams: "",

    // funnel + membership fields
    stage: "prospect", // crm_contacts.stage
    status: "prospect", // memberships.status
    tier: "Explore",
    billing: "None",

    // auto days + bonus
    bonus_days: 0,

    age_verified: false,
    can_book: false,

    note: "",
  });

  // Emergency Contact
  const [emergency, setEmergency] = useState({
    name: "",
    relationship: "",
    phone: "",
    email: "",
  });

  // Drivers
  const [drivers, setDrivers] = useState([newDriver(true)]);

  // Documents (files) - keep in state, uploaded on submit
  const [insuranceFile, setInsuranceFile] = useState(null); // contact-level
  const [insuranceExpiresOn, setInsuranceExpiresOn] = useState(""); // optional

  // per driver docs
  const [driverDocs, setDriverDocs] = useState(() => ({
    // [idx]: { dl_front, dl_back, abstract, boating }
  }));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setEmerg = (k, v) => setEmergency((p) => ({ ...p, [k]: v }));

  const baseDays = useMemo(() => DAYS_BY_TIER[form.tier] ?? 0, [form.tier]);

  const computedDaysRemaining = useMemo(() => {
    if (form.status !== "active") return 0;
    const bonus = toInt(form.bonus_days) || 0;
    return Math.max(0, baseDays + bonus);
  }, [form.status, baseDays, form.bonus_days]);

  function setDriverField(i, k, v) {
    setDrivers((prev) => prev.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
  }

  function addDriver() {
    setDrivers((prev) => [...prev, newDriver(false)]);
  }

  function removeDriver(i) {
    setDrivers((prev) =>
      prev
        .filter((_, idx) => idx !== i)
        .map((d, idx) => ({ ...d, is_primary: idx === 0 }))
    );

    setDriverDocs((prev) => {
      const next = { ...prev };
      delete next[i];

      // re-index docs after removal
      const re = {};
      Object.entries(next).forEach(([k, val]) => {
        const oldIdx = Number(k);
        if (Number.isNaN(oldIdx)) return;
        const newIdx = oldIdx > i ? oldIdx - 1 : oldIdx;
        re[newIdx] = val;
      });
      return re;
    });
  }

  function setDriverDoc(i, key, file) {
    setDriverDocs((prev) => ({
      ...prev,
      [i]: { ...(prev[i] || {}), [key]: file },
    }));
  }

  async function uploadToMemberDocs({ contactId, driverId, docType, file }) {
    if (!file) return null;

    const now = Date.now();
    const ext = fileExt(file.name);
    const safeName = makeSafeFileName(file.name || `upload.${ext}`);
    const path = [
      "contact",
      contactId,
      driverId ? `driver_${driverId}` : "contact",
      docType,
      `${now}_${safeName}`,
    ].join("/");

    const { error: upErr } = await supabase.storage.from("member-docs").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

    if (upErr) throw upErr;
    return path;
  }

  async function insertDocumentRow(row) {
    // Force a response so errors can’t be swallowed
    const { error } = await supabase.from("contact_documents").insert(row);
    if (error) throw error;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErrorObj(null);
    setCreated({ contactId: null, membershipId: null, driverIds: [] });
    setBusy(true);

    try {
      // 1) Create CRM Contact
      const contactPayload = {
        first_name: toNull(form.first_name),
        last_name: toNull(form.last_name),
        email: safeLowerEmail(form.email),
        phone: toNull(form.phone),
        stage: form.stage,
        source: toNull(form.source),

        date_of_birth: ymd(form.date_of_birth),

        // jsonb column "format" assumed on crm_contacts
        format: {
          family: toNull(form.format_family),
          occupation: toNull(form.format_occupation),
          recreation: toNull(form.format_recreation),
          motivation: toNull(form.format_motivation),
          animals: toNull(form.format_animals),
          teams: toNull(form.format_teams),
        },

        notes: form.note
          ? [{ at: new Date().toISOString().slice(0, 10), text: normalize(form.note) }]
          : [],
      };

      const { data: contact, error: contactErr } = await supabase
        .from("crm_contacts")
        .insert(contactPayload)
        .select("id")
        .single();

      if (contactErr) throw contactErr;
      if (!contact?.id) throw new Error("crm_contacts insert returned no id.");

      const contactId = contact.id;
      setCreated((p) => ({ ...p, contactId }));

      // 2) Emergency contact (optional)
      const emergHasAny =
        normalize(emergency.name) ||
        normalize(emergency.relationship) ||
        normalize(emergency.phone) ||
        normalize(emergency.email);

      if (emergHasAny) {
        const emergPayload = {
          contact_id: contactId,
          name: toNull(emergency.name),
          relationship: toNull(emergency.relationship),
          phone: toNull(emergency.phone),
          email: safeLowerEmail(emergency.email),
        };

        const { error: emergErr } = await supabase.from("contact_emergency").insert(emergPayload);
        if (emergErr) throw emergErr;
      }

      // 3) Membership linked to that contact
      const membershipPayload = {
        contact_id: contactId,
        status: form.status,
        tier: form.tier,
        billing: form.billing,
        days_remaining: computedDaysRemaining,
        age_verified: !!form.age_verified,
        can_book: !!form.can_book,
      };

      const { data: memRow, error: memErr } = await supabase
        .from("memberships")
        .insert(membershipPayload)
        .select("id")
        .single();

      if (memErr) throw memErr;
      if (!memRow?.id) throw new Error("memberships insert returned no id.");

      setCreated((p) => ({ ...p, membershipId: memRow.id }));

      // 4) Drivers (at least primary)
      const driverRowsToInsert = drivers.map((d, idx) => ({
        contact_id: contactId,
        is_primary: idx === 0,

        first_name: toNull(d.first_name),
        last_name: toNull(d.last_name),
        phone: toNull(d.phone),
        email: safeLowerEmail(d.email),

        driver_license_number: toNull(d.driver_license_number),
        driver_license_expiry: ymd(d.driver_license_expiry),
        driver_license_province: toNull(d.driver_license_province),

        abstract_required: !!d.abstract_required,

        boating_license_number: toNull(d.boating_license_number),
        boating_license_expiry: ymd(d.boating_license_expiry),
      }));

      // NOTE: select returns rows in an unspecified order; we assume it matches insert order in practice,
      // but to be safe we can map by index if Supabase returns in same order (most common).
      const { data: insertedDrivers, error: driversErr } = await supabase
        .from("contact_drivers")
        .insert(driverRowsToInsert)
        .select("id");

      if (driversErr) throw driversErr;

      const driverIds = (insertedDrivers || []).map((r) => r.id).filter(Boolean);
      setCreated((p) => ({ ...p, driverIds }));

      // 5) Upload documents + insert contact_documents rows

      // 5a) Proof of insurance (contact-level)
      if (insuranceFile) {
        const file_path = await uploadToMemberDocs({
          contactId,
          driverId: null,
          docType: DOC_TYPES.proof_of_insurance,
          file: insuranceFile,
        });

        await insertDocumentRow({
          contact_id: contactId,
          driver_id: null,
          doc_type: DOC_TYPES.proof_of_insurance,
          file_path,
          file_name: insuranceFile.name || null,
          mime_type: insuranceFile.type || null,
          expires_on: ymd(insuranceExpiresOn),
        });
      }

      // 5b) Per-driver documents
      for (let i = 0; i < driverIds.length; i++) {
        const driverId = driverIds[i];
        const docs = driverDocs[i] || {};

        // Driver’s license front
        if (docs.dl_front) {
          const path = await uploadToMemberDocs({
            contactId,
            driverId,
            docType: DOC_TYPES.drivers_license_front,
            file: docs.dl_front,
          });

          await insertDocumentRow({
            contact_id: contactId,
            driver_id: driverId,
            doc_type: DOC_TYPES.drivers_license_front,
            file_path: path,
            file_name: docs.dl_front.name || null,
            mime_type: docs.dl_front.type || null,
            expires_on: null,
          });
        }

        // Driver’s license back
        if (docs.dl_back) {
          const path = await uploadToMemberDocs({
            contactId,
            driverId,
            docType: DOC_TYPES.drivers_license_back,
            file: docs.dl_back,
          });

          await insertDocumentRow({
            contact_id: contactId,
            driver_id: driverId,
            doc_type: DOC_TYPES.drivers_license_back,
            file_path: path,
            file_name: docs.dl_back.name || null,
            mime_type: docs.dl_back.type || null,
            expires_on: null,
          });
        }

        // Abstract
        if (docs.abstract) {
          const path = await uploadToMemberDocs({
            contactId,
            driverId,
            docType: DOC_TYPES.drivers_abstract,
            file: docs.abstract,
          });

          await insertDocumentRow({
            contact_id: contactId,
            driver_id: driverId,
            doc_type: DOC_TYPES.drivers_abstract,
            file_path: path,
            file_name: docs.abstract.name || null,
            mime_type: docs.abstract.type || null,
            expires_on: null,
          });
        }

        // Boating license (file) + expires_on from driver field
        if (docs.boating) {
          const path = await uploadToMemberDocs({
            contactId,
            driverId,
            docType: DOC_TYPES.boating_license,
            file: docs.boating,
          });

          await insertDocumentRow({
            contact_id: contactId,
            driver_id: driverId,
            doc_type: DOC_TYPES.boating_license,
            file_path: path,
            file_name: docs.boating.name || null,
            mime_type: docs.boating.type || null,
            expires_on: ymd(drivers[i]?.boating_license_expiry),
          });
        }
      }

      // Success
      nav("/admin/members");
    } catch (err) {
      // important: log full error to console too
      // eslint-disable-next-line no-console
      console.error("NewMember save failed:", err);

      setErrorObj(formatSbError(err));
    } finally {
      setBusy(false);
    }
  }
  

  const badgeTone = form.status === "active" ? "success" : form.status === "prospect" ? "warning" : "neutral";

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <div className="page__back">
            <Link className="link" to="/admin/dashboard">← Dashboard</Link>
          </div>
          <h1 className="page__title">Add Member</h1>
          <div className="page__sub">
            Create a prospect or active member (contact + membership + drivers + docs).
          </div>
        </div>

        <Badge tone={badgeTone}>{String(form.status).toUpperCase()}</Badge>
      </div>

      <form onSubmit={onSubmit} className="grid grid--2" noValidate>
        {/* LEFT COLUMN */}
        <div className="stack">
          <Card title="Member details" subtitle="Saved to crm_contacts">
            <div className="formGrid">
              <div className="formRow2">
                <div>
                  <div className="memLabel">First name</div>
                  <input
                    className="memInput"
                    value={form.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <div className="memLabel">Last name</div>
                  <input
                    className="memInput"
                    value={form.last_name}
                    onChange={(e) => set("last_name", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Email</div>
                  <input
                    className="memInput"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="optional"
                  />
                </div>
                <div>
                  <div className="memLabel">Phone</div>
                  <input
                    className="memInput"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="optional"
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Date of birth</div>
                  <input
                    className="memInput"
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => set("date_of_birth", e.target.value)}
                  />
                  <div className="memMuted" style={{ marginTop: 6 }}>
                    Age is calculated from DOB (recommended) instead of storing Age.
                  </div>
                </div>
                <div>
                  <div className="memLabel">Source</div>
                  <input
                    className="memInput"
                    value={form.source}
                    onChange={(e) => set("source", e.target.value)}
                    placeholder="call-in / website / boat show / referral…"
                  />
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">CRM Stage</div>
                  <select className="memSelect" value={form.stage} onChange={(e) => set("stage", e.target.value)}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="memLabel">Internal note</div>
                  <input
                    className="memInput"
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    placeholder="optional"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card title="F.O.R.M.A.T" subtitle="Saved to crm_contacts.format (json)">
            <div className="formGrid">
              <div>
                <div className="memLabel">Family</div>
                <input className="memInput" value={form.format_family} onChange={(e) => set("format_family", e.target.value)} />
              </div>
              <div>
                <div className="memLabel">Occupation</div>
                <input className="memInput" value={form.format_occupation} onChange={(e) => set("format_occupation", e.target.value)} />
              </div>
              <div>
                <div className="memLabel">Recreation</div>
                <input className="memInput" value={form.format_recreation} onChange={(e) => set("format_recreation", e.target.value)} />
              </div>
              <div>
                <div className="memLabel">Motivation</div>
                <input className="memInput" value={form.format_motivation} onChange={(e) => set("format_motivation", e.target.value)} />
              </div>
              <div className="formRow2">
                <div>
                  <div className="memLabel">Animals</div>
                  <input className="memInput" value={form.format_animals} onChange={(e) => set("format_animals", e.target.value)} />
                </div>
                <div>
                  <div className="memLabel">Teams</div>
                  <input className="memInput" value={form.format_teams} onChange={(e) => set("format_teams", e.target.value)} />
                </div>
              </div>
            </div>
          </Card>

          <Card title="Emergency contact" subtitle="Saved to contact_emergency (optional)">
            <div className="formGrid">
              <div className="formRow2">
                <div>
                  <div className="memLabel">Name</div>
                  <input className="memInput" value={emergency.name} onChange={(e) => setEmerg("name", e.target.value)} />
                </div>
                <div>
                  <div className="memLabel">Relationship</div>
                  <input className="memInput" value={emergency.relationship} onChange={(e) => setEmerg("relationship", e.target.value)} />
                </div>
              </div>
              <div className="formRow2">
                <div>
                  <div className="memLabel">Phone</div>
                  <input className="memInput" value={emergency.phone} onChange={(e) => setEmerg("phone", e.target.value)} />
                </div>
                <div>
                  <div className="memLabel">Email</div>
                  <input className="memInput" type="email" value={emergency.email} onChange={(e) => setEmerg("email", e.target.value)} />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="stack">
          <Card title="Membership" subtitle="Saved to memberships">
            <div className="formGrid">
              <div className="formRow2">
                <div>
                  <div className="memLabel">Membership Status</div>
                  <select
                    className="memSelect"
                    value={form.status}
                    onChange={(e) => {
                      const next = e.target.value;
                      set("status", next);

                      if (next === "prospect") {
                        set("billing", "None");
                        set("can_book", false);
                        set("age_verified", false);
                        set("bonus_days", 0);
                      }
                      if (next === "active") {
                        set("billing", form.billing === "None" ? "Monthly" : form.billing);
                      }
                    }}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="memLabel">Tier</div>
                  <select className="memSelect" value={form.tier} onChange={(e) => set("tier", e.target.value)}>
                    {TIERS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Billing</div>
                  <select
                    className="memSelect"
                    value={form.billing}
                    onChange={(e) => set("billing", e.target.value)}
                    disabled={form.status === "prospect"}
                  >
                    {BILLING.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="memLabel">Bonus days (add days)</div>
                  <input
                    className="memInput"
                    type="number"
                    min="0"
                    value={form.bonus_days}
                    onChange={(e) => set("bonus_days", e.target.value)}
                    disabled={form.status !== "active"}
                  />
                </div>
              </div>

              <div className="memInfoBlock">
                <div className="memKV">
                  <span>Base days for tier</span>
                  <div className="memKV__v">{form.status === "active" ? baseDays : 0}</div>
                </div>
                <div className="memKV">
                  <span>Days remaining (saved)</span>
                  <div className="memKV__v">{computedDaysRemaining}</div>
                </div>
              </div>

              <label className="checkRow">
                <input
                  type="checkbox"
                  checked={form.age_verified}
                  onChange={(e) => set("age_verified", e.target.checked)}
                  disabled={form.status !== "active"}
                />
                <span>Age verified (21+)</span>
              </label>

              <label className="checkRow">
                <input
                  type="checkbox"
                  checked={form.can_book}
                  onChange={(e) => set("can_book", e.target.checked)}
                  disabled={form.status !== "active"}
                />
                <span>Can book vehicles (only for Active)</span>
              </label>

              <div className="memMuted">
                Prospects are saved to the funnel but cannot book. To allow booking: set Status to{" "}
                <b>active</b>, check <b>Age verified</b>, and enable <b>Can book</b>.
              </div>
            </div>
          </Card>

          <Card title="Drivers" subtitle="Saved to contact_drivers + docs to member-docs">
            <div className="stack">
              <div className="memMuted">
                Driver #1 is treated as the <b>Primary</b> driver automatically.
              </div>

              {drivers.map((d, idx) => {
                const docs = driverDocs[idx] || {};
                return (
                  <div key={idx} className="memInfoBlock">
                    <div className="memBlockTitle" style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span>{idx === 0 ? "Primary Driver" : `Additional Driver #${idx + 1}`}</span>
                      {idx > 0 && (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => removeDriver(idx)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="formGrid">
                      <div className="formRow2">
                        <div>
                          <div className="memLabel">First name</div>
                          <input className="memInput" value={d.first_name} onChange={(e) => setDriverField(idx, "first_name", e.target.value)} />
                        </div>
                        <div>
                          <div className="memLabel">Last name</div>
                          <input className="memInput" value={d.last_name} onChange={(e) => setDriverField(idx, "last_name", e.target.value)} />
                        </div>
                      </div>

                      <div className="formRow2">
                        <div>
                          <div className="memLabel">Phone</div>
                          <input className="memInput" value={d.phone} onChange={(e) => setDriverField(idx, "phone", e.target.value)} />
                        </div>
                        <div>
                          <div className="memLabel">Email</div>
                          <input className="memInput" type="email" value={d.email} onChange={(e) => setDriverField(idx, "email", e.target.value)} />
                        </div>
                      </div>

                      <div className="formRow2">
                        <div>
                          <div className="memLabel">Driver’s License #</div>
                          <input className="memInput" value={d.driver_license_number} onChange={(e) => setDriverField(idx, "driver_license_number", e.target.value)} />
                        </div>
                        <div>
                          <div className="memLabel">License Expiration</div>
                          <input className="memInput" type="date" value={d.driver_license_expiry} onChange={(e) => setDriverField(idx, "driver_license_expiry", e.target.value)} />
                        </div>
                      </div>

                      <div>
                        <div className="memLabel">Province/State</div>
                        <input className="memInput" value={d.driver_license_province} onChange={(e) => setDriverField(idx, "driver_license_province", e.target.value)} />
                      </div>

                      <label className="checkRow">
                        <input
                          type="checkbox"
                          checked={!!d.abstract_required}
                          onChange={(e) => setDriverField(idx, "abstract_required", e.target.checked)}
                        />
                        <span>Abstract required</span>
                      </label>

                      <div className="formRow2">
                        <div>
                          <div className="memLabel">Boating License #</div>
                          <input className="memInput" value={d.boating_license_number} onChange={(e) => setDriverField(idx, "boating_license_number", e.target.value)} />
                        </div>
                        <div>
                          <div className="memLabel">Boating License Expiration</div>
                          <input className="memInput" type="date" value={d.boating_license_expiry} onChange={(e) => setDriverField(idx, "boating_license_expiry", e.target.value)} />
                        </div>
                      </div>

                      <hr style={{ border: 0, borderTop: "1px solid rgba(255,255,255,.08)" }} />

                      <div className="formGrid">
                        <div className="formRow2">
                          <div>
                            <div className="memLabel">Driver’s License (front)</div>
                            <input
                              className="memInput"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setDriverDoc(idx, "dl_front", e.target.files?.[0] || null)}
                            />
                            <div className="memMuted">{docs.dl_front ? docs.dl_front.name : "optional"}</div>
                          </div>
                          <div>
                            <div className="memLabel">Driver’s License (back)</div>
                            <input
                              className="memInput"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setDriverDoc(idx, "dl_back", e.target.files?.[0] || null)}
                            />
                            <div className="memMuted">{docs.dl_back ? docs.dl_back.name : "optional"}</div>
                          </div>
                        </div>

                        <div className="formRow2">
                          <div>
                            <div className="memLabel">Driver Abstract (PDF/Screenshot)</div>
                            <input
                              className="memInput"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setDriverDoc(idx, "abstract", e.target.files?.[0] || null)}
                            />
                            <div className="memMuted">{docs.abstract ? docs.abstract.name : "optional"}</div>
                          </div>
                          <div>
                            <div className="memLabel">Boating License (image/pdf)</div>
                            <input
                              className="memInput"
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setDriverDoc(idx, "boating", e.target.files?.[0] || null)}
                            />
                            <div className="memMuted">{docs.boating ? docs.boating.name : "optional"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" className="btn btn--primary" onClick={addDriver} disabled={busy}>
                  + Add Driver
                </button>
              </div>
            </div>
          </Card>

          <Card title="Documents" subtitle="Uploads to member-docs + saved to contact_documents">
            <div className="formGrid">
              <div>
                <div className="memLabel">Proof of Insurance (PDF/Screenshot)</div>
                <input
                  className="memInput"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setInsuranceFile(e.target.files?.[0] || null)}
                />
                <div className="memMuted">{insuranceFile ? insuranceFile.name : "optional"}</div>
              </div>

              <div className="formRow2">
                <div>
                  <div className="memLabel">Insurance Expiration (optional)</div>
                  <input
                    className="memInput"
                    type="date"
                    value={insuranceExpiresOn}
                    onChange={(e) => setInsuranceExpiresOn(e.target.value)}
                  />
                </div>
                <div className="memMuted" style={{ alignSelf: "end" }}>
                  Tip: driver/boating expirations are stored on each driver record.
                </div>
              </div>
            </div>
          </Card>

          {/* DEBUG + ERRORS */}
          {(created.contactId || created.membershipId || (created.driverIds || []).length) && (
            <div className="memMuted">
              Created so far:{" "}
              {created.contactId ? <span>contact_id={created.contactId} </span> : null}
              {created.membershipId ? <span>membership_id={created.membershipId} </span> : null}
              {created.driverIds?.length ? <span>drivers={created.driverIds.length}</span> : null}
            </div>
          )}

          {errorObj && (
            <div className="adminAuth__error" style={{ marginTop: 8 }}>
              <div><b>{errorObj.message}</b></div>
              {errorObj.code ? <div>Code: {errorObj.code}</div> : null}
              {errorObj.details ? <div>Details: {errorObj.details}</div> : null}
              {errorObj.hint ? <div>Hint: {errorObj.hint}</div> : null}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn--primary" type="submit" disabled={busy}>
              {busy ? "Saving…" : "Create Member"}
            </button>
            <Link className="btn" to="/admin/members">
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}